import type { ImageView } from '@vizit/api';
import { ScoreBar, Separator, Skeleton, Text, TextColor, TextVariant } from '@vizit/atoms';

export const MOBILE_METRICS = [
  { field: 'mobile_brand_clarity' as const, label: 'Brand' },
  { field: 'mobile_product_type_clarity' as const, label: 'Product type' },
  { field: 'mobile_variant_clarity' as const, label: 'Variant' },
  { field: 'mobile_count_clarity' as const, label: 'Count' },
  { field: 'mobile_size_clarity' as const, label: 'Size' },
] as const;

type MobileField = (typeof MOBILE_METRICS)[number]['field'];

function getImageField(imageData: ImageView | null, field: string): unknown {
  if (!imageData) return undefined;
  return (imageData as Record<string, unknown>)[field];
}

function getMobileScore(imageData: ImageView | null, field: MobileField): number | null {
  const value = getImageField(imageData, field);
  if (value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num;
}

function hasMobileData(imageData: ImageView | null): boolean {
  if (!imageData) return false;
  return MOBILE_METRICS.some((metric) => getMobileScore(imageData, metric.field) !== null);
}

function MobileReadinessMetricRow({ label, score }: { label: string; score: number | null }) {
  return (
    <div className="flex w-full items-center gap-3">
      <Text
        variant={TextVariant.MonoSm}
        color={TextColor.Tertiary}
        showEllipsis={false}
        elementType="span"
        className="w-[124px] shrink-0 uppercase"
      >
        {label}
      </Text>
      <div className="min-w-0 flex-1">
        <ScoreBar score={score} labelType="likert" isMobile isUnscored={score === null} />
      </div>
    </div>
  );
}

function MobileReadinessLoading() {
  return (
    <div className="flex flex-col gap-10 bg-secondary-alt px-8 py-10">
      <div className="flex flex-col gap-1">
        <Text variant={TextVariant.SmMedium} color={TextColor.Primary} showEllipsis={false} elementType="span">
          Mobile-Readiness
        </Text>
        <Text variant={TextVariant.SmRegular} color={TextColor.Quaternary} showEllipsis={false} elementType="p">
          {`Clarity and legibility of the ${MOBILE_METRICS.length} shopper questions at mobile thumbnail size.`}
        </Text>
      </div>
      <div className="flex flex-col gap-2">
        {MOBILE_METRICS.map((metric) => (
          <div key={metric.field} className="flex w-full items-center gap-3">
            <Text
              variant={TextVariant.MonoSm}
              color={TextColor.Tertiary}
              showEllipsis={false}
              elementType="span"
              className="w-[124px] shrink-0 uppercase"
            >
              {metric.label}
            </Text>
            <div className="flex min-w-0 flex-1 items-center h-[27px]">
              <Skeleton className="h-[6px] w-full rounded-[1px]" variant="square" />
            </div>
          </div>
        ))}
      </div>
      <Separator />
      <Text
        variant={TextVariant.XsRegular}
        color={TextColor.Quaternary}
        showEllipsis={false}
        elementType="p"
        className="italic"
      >
        Diagnostic only — does not affect your hero Vizit score.
      </Text>
    </div>
  );
}

function MobileReadinessPanel({ imageData }: { imageData: ImageView | null }) {
  if (!hasMobileData(imageData)) return null;

  return (
    <div className="flex flex-col gap-10 bg-secondary-alt px-8 py-10">
      <div className="flex flex-col gap-1">
        <Text variant={TextVariant.SmMedium} color={TextColor.Primary} showEllipsis={false} elementType="span">
          Mobile-Readiness
        </Text>
        <Text variant={TextVariant.SmRegular} color={TextColor.Quaternary} showEllipsis={false} elementType="p">
          {`Clarity and legibility of the ${MOBILE_METRICS.length} shopper questions at mobile thumbnail size.`}
        </Text>
      </div>
      <div className="flex flex-col gap-2">
        {MOBILE_METRICS.map((metric) => (
          <MobileReadinessMetricRow
            key={metric.field}
            label={metric.label}
            score={getMobileScore(imageData, metric.field)}
          />
        ))}
      </div>
      <Separator />
      <Text
        variant={TextVariant.XsRegular}
        color={TextColor.Quaternary}
        showEllipsis={false}
        elementType="p"
        className="italic"
      >
        Diagnostic only — does not affect your hero Vizit score.
      </Text>
    </div>
  );
}

export { MobileReadinessPanel, MobileReadinessLoading, hasMobileData };
